import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { removeToken } from '@/requestErrorConfig';
import HeaderDropdown from '../HeaderDropdown';
import { ModalForm, ProFormText } from '@ant-design/pro-components';
import { changePassword, updateAdminInfo } from '@/services/admin';
import { message } from 'antd';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.nickname || currentUser?.username || '用户'}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    // 清除 token
    removeToken();

    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [userInfoModalVisible, setUserInfoModalVisible] = useState(false);

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'modifyPassword') {
      setPasswordModalVisible(true);
      return;
    }
    if (key === 'modifyUserInfo') {
      setUserInfoModalVisible(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || (!currentUser.username && !currentUser.nickname)) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
        {
          key: 'center',
          icon: <UserOutlined />,
          label: '个人中心',
        },
        {
          key: 'settings',
          icon: <SettingOutlined />,
          label: '个人设置',
        },
        {
          type: 'divider' as const,
        },
      ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
    {
      key: 'modifyPassword',
      icon: <SettingOutlined />,
      label: '修改密码',
    },
    {
      key: 'modifyUserInfo',
      icon: <UserOutlined />,
      label: '修改用户信息',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      <ModalForm
        title="修改密码"
        width={400}
        open={passwordModalVisible}
        onOpenChange={setPasswordModalVisible}
        onFinish={async (values) => {
          if (values.newPassword !== values.confirmPassword) {
            message.error('两次输入的密码不一致！');
            return false;
          }
          try {
            await changePassword({
              old_password: values.oldPassword,
              new_password: values.newPassword,
            });
            message.success('密码修改成功，请重新登录！');
            // 退出登录
            loginOut();
            return true;
          } catch (error) {
            console.error(error);
            return false;
          }
        }}
      >
        <ProFormText.Password
          name="oldPassword"
          label="旧密码"
          rules={[
            {
              required: true,
              message: '请输入旧密码！',
            },
          ]}
        />
        <ProFormText.Password
          name="newPassword"
          label="新密码"
          rules={[
            {
              required: true,
              message: '请输入新密码！',
            },
            {
              min: 6,
              message: '密码长度不能少于6位！',
            },
          ]}
        />
        <ProFormText.Password
          name="confirmPassword"
          label="确认新密码"
          rules={[
            {
              required: true,
              message: '请再次输入新密码！',
            },
          ]}
        />
      </ModalForm>
      <ModalForm
        title="修改用户信息"
        width={400}
        open={userInfoModalVisible}
        onOpenChange={setUserInfoModalVisible}
        initialValues={{
          nickname: currentUser?.nickname,
          email: currentUser?.email,
        }}
        onFinish={async (values) => {
          try {
            await updateAdminInfo(values);
            message.success('信息修改成功！');
            // 刷新用户信息
            const admin = await initialState?.fetchUserInfo?.();
            if (admin) {
              await setInitialState((s) => ({
                ...s,
                currentUser: admin,
              }));
            }
            return true;
          } catch (error) {
            console.error(error);
            return false;
          }
        }}
      >
        <ProFormText
          name="nickname"
          label="昵称"
          rules={[
            {
              required: true,
              message: '请输入昵称！',
            },
          ]}
        />
        <ProFormText
          name="email"
          label="邮箱"
          rules={[
            {
              type: 'email',
              message: '请输入有效的邮箱地址！',
            },
          ]}
        />
      </ModalForm>
    </>
  );
};
